export type TicketAttachmentKind = 'image' | 'video'

// Ticket attachments use stricter limits than the public admin library because every file is
// private support data and every upload passes through the application backend.
export const MAX_TICKET_ATTACHMENTS = 10
export const MAX_TICKET_IMAGE_BYTES = 5 * 1024 * 1024
export const MAX_TICKET_VIDEO_BYTES = 25 * 1024 * 1024
export const MAX_TICKET_TOTAL_BYTES = 100 * 1024 * 1024
export const TICKET_ATTACHMENT_ACCEPT = 'image/png,image/jpeg,video/mp4,video/webm'

// MIME and extension must agree when MIME is available. Server-side magic/container checks
// still decide whether the upload is genuinely an image or video.
const supportedTypes: Record<string, { extensions: string[], kind: TicketAttachmentKind }> = {
  'image/png': { extensions: ['.png'], kind: 'image' },
  'image/jpeg': { extensions: ['.jpg', '.jpeg'], kind: 'image' },
  'video/mp4': { extensions: ['.mp4'], kind: 'video' },
  'video/webm': { extensions: ['.webm'], kind: 'video' },
}

// extensionOf returns the normalized final suffix used for the allowlist comparison.
function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot >= 0 ? name.slice(dot).toLowerCase() : ''
}

// ticketAttachmentKind drives image-versus-video previews and accepts extension-only
// classification when a browser does not populate File.type.
export function ticketAttachmentKind(file: Pick<File, 'name' | 'type'>): TicketAttachmentKind | null {
  const extension = extensionOf(file.name)
  if (file.type) {
    const media = supportedTypes[file.type.toLowerCase()]
    return media?.extensions.includes(extension) ? media.kind : null
  }
  return Object.values(supportedTypes).find(media => media.extensions.includes(extension))?.kind ?? null
}

// ticketAttachmentValidationError mirrors backend format and per-kind size checks so the user
// receives immediate feedback without treating client validation as a security control.
export function ticketAttachmentValidationError(file: Pick<File, 'name' | 'type' | 'size'>): string | null {
  const kind = ticketAttachmentKind(file)
  if (!kind) {
    return 'Only PNG, JPG, MP4 and WebM attachments are supported'
  }
  if (kind === 'image' && file.size > MAX_TICKET_IMAGE_BYTES) {
    return 'Each image must be 5MB or smaller'
  }
  if (kind === 'video' && file.size > MAX_TICKET_VIDEO_BYTES) {
    return 'Each video must be 25MB or smaller'
  }
  return null
}
